"use client"

import { Menu, X } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useRef, useState } from "react"

import { logoPosi } from "@/assets/logos"
import { ContactLink } from "@/components/layout/ContactLink"
import { contactHref } from "@/data/contact"

// 会社共通の入口。専門情報は対応する事業ページから案内する。
const NAV_ITEMS = [
  { href: "/business", label: "事業紹介" },
  { href: "/gallery", label: "作品ギャラリー" },
  { href: "/guides", label: "お役立ちガイド" },
  { href: "/company", label: "会社情報" },
] as const

const CONTACT_FORM_URL = contactHref()

export function TopNav() {
  const [isOpen, setIsOpen] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)

  const close = () => setIsOpen(false)

  return (
    <header
      className="yo-nav sticky top-0 z-50"
      onKeyDown={(event) => {
        if (event.key === "Escape" && isOpen) {
          close()
          menuButtonRef.current?.focus()
        }
      }}
      style={{
        backdropFilter: "saturate(160%) blur(14px)",
        WebkitBackdropFilter: "saturate(160%) blur(14px)",
        background: "rgba(255,255,255,0.92)",
        borderBottom: "1px solid var(--yo-line)",
      }}
    >
      <div className="yo-container flex items-center justify-between h-16">
        <Link
          href="/"
          className="flex items-center shrink-0"
          onClick={close}
          aria-label="YOSHINANI トップへ"
        >
          {/*
            表示幅は 124px。SVG は画像最適化を通らないため unoptimized で
            srcset を出さず、原本 1 本だけを preload する。
          */}
          <Image
            src={logoPosi}
            alt="YOSHINANI トップへ"
            className="w-[124px] h-auto shrink-0"
            unoptimized
            priority
          />
        </Link>

        {/* デスクトップナビ */}
        <nav
          aria-label="メインナビゲーション"
          className="hidden lg:flex items-center gap-6"
        >
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.href}
              href={item.href}
              className="text-sm font-bold tracking-wide transition-opacity hover:opacity-60"
              style={{ color: "var(--yo-ink)" }}
            >
              {item.label}
            </Link>
          ))}
          <ContactLink href={CONTACT_FORM_URL} className="yo-btn">
            相談・見積もり
          </ContactLink>
        </nav>

        {/* モバイル開閉ボタン */}
        <button
          type="button"
          ref={menuButtonRef}
          className="lg:hidden -mr-2 flex size-11 items-center justify-center rounded-lg"
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={isOpen ? "メニューを閉じる" : "メニューを開く"}
          aria-expanded={isOpen}
          aria-controls="mobile-navigation"
        >
          {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
        </button>
      </div>

      {/* モバイルメニュー */}
      {isOpen && (
        <nav
          id="mobile-navigation"
          aria-label="モバイルナビゲーション"
          className="lg:hidden max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain"
          style={{
            borderTop: "1px solid var(--yo-line)",
            background: "var(--yo-bg)",
          }}
        >
          <ul className="yo-container flex flex-col py-4">
            {NAV_ITEMS.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  className="block py-3 text-base font-bold"
                  onClick={close}
                >
                  {item.label}
                </Link>
              </li>
            ))}
            <li className="pt-3">
              <ContactLink
                href={CONTACT_FORM_URL}
                className="yo-btn w-full justify-center"
                onClick={close}
              >
                相談・見積もり
              </ContactLink>
            </li>
          </ul>
        </nav>
      )}
    </header>
  )
}
